import { useCallback, useEffect, useState } from 'react';

export type SoundName =
  | 'pointerCrossedTarget'
  | 'targetPressed'
  | 'sceneChanged'
  | 'purchaseStamped'
  | 'ownershipGranted'
  | 'requestRefused'
  | 'installFinished'
  | 'identSounded';

interface ToneLayer {
  waveform: OscillatorType;
  fromHz: number;
  toHz: number;
  peakGain: number;
  startAfterSeconds: number;
  durationSeconds: number;
}

interface NoiseLayer {
  fromCutoffHz: number;
  toCutoffHz: number;
  peakGain: number;
  durationSeconds: number;
}

interface SoundVoice {
  tones: readonly ToneLayer[];
  noise?: NoiseLayer;
}

const tone = (
  waveform: OscillatorType,
  fromHz: number,
  toHz: number,
  peakGain: number,
  durationSeconds: number,
  startAfterSeconds = 0,
): ToneLayer => ({ waveform, fromHz, toHz, peakGain, durationSeconds, startAfterSeconds });

const SOUND_LIBRARY: Record<SoundName, SoundVoice> = {
  pointerCrossedTarget: {
    tones: [tone('sine', 2400, 2050, 0.05, 0.04)],
  },
  targetPressed: {
    tones: [tone('square', 880, 620, 0.1, 0.06), tone('sine', 1760, 1760, 0.035, 0.03)],
  },
  sceneChanged: {
    tones: [],
    noise: { fromCutoffHz: 420, toCutoffHz: 6400, peakGain: 0.07, durationSeconds: 0.34 },
  },
  purchaseStamped: {
    tones: [tone('square', 150, 68, 0.3, 0.2), tone('sine', 1500, 1180, 0.07, 0.13, 0.01)],
  },
  ownershipGranted: {
    tones: [
      tone('sine', 392, 392, 0.13, 0.62),
      tone('sine', 587, 587, 0.1, 0.58, 0.08),
      tone('sine', 784, 784, 0.08, 0.54, 0.16),
      tone('triangle', 196, 196, 0.07, 0.7),
    ],
  },
  requestRefused: {
    tones: [tone('square', 184, 116, 0.18, 0.24)],
  },
  installFinished: {
    tones: [
      tone('triangle', 523, 523, 0.13, 0.22),
      tone('triangle', 659, 659, 0.13, 0.22, 0.09),
      tone('triangle', 784, 784, 0.15, 0.42, 0.18),
    ],
  },
  identSounded: {
    tones: [
      tone('triangle', 330, 330, 0.11, 0.18),
      tone('triangle', 392, 392, 0.11, 0.18, 0.1),
      tone('triangle', 494, 494, 0.11, 0.18, 0.2),
      tone('triangle', 587, 587, 0.11, 0.18, 0.3),
      tone('triangle', 698, 698, 0.16, 0.7, 0.4),
      tone('sine', 174, 174, 0.09, 1.1),
    ],
  },
};

const SOUND_PREFERENCE_STORAGE_KEY = 'gb.sound.enabled';
const SILENCE_FLOOR_GAIN = 0.0001;
const NOISE_BUFFER_SECONDS = 1;

function readStoredSoundPreference(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SOUND_PREFERENCE_STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

let soundIsEnabled = readStoredSoundPreference();
let sharedAudioContext: AudioContext | null = null;
let sharedNoiseBuffer: AudioBuffer | null = null;
const soundPreferenceListeners = new Set<(enabled: boolean) => void>();

function resolveAudioContextConstructor(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null;
  const legacy = (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  return window.AudioContext ?? legacy ?? null;
}

function openAudioContext(): AudioContext | null {
  const AudioContextConstructor = resolveAudioContextConstructor();
  if (!AudioContextConstructor) return null;

  sharedAudioContext ??= new AudioContextConstructor();
  if (sharedAudioContext.state === 'suspended') void sharedAudioContext.resume();
  return sharedAudioContext;
}

function noiseBufferFor(audio: AudioContext): AudioBuffer {
  if (sharedNoiseBuffer) return sharedNoiseBuffer;

  const sampleCount = Math.floor(audio.sampleRate * NOISE_BUFFER_SECONDS);
  const buffer = audio.createBuffer(1, sampleCount, audio.sampleRate);
  const samples = buffer.getChannelData(0);
  for (let index = 0; index < sampleCount; index += 1) samples[index] = Math.random() * 2 - 1;

  sharedNoiseBuffer = buffer;
  return buffer;
}

function scheduleEnvelope(
  gain: GainNode,
  startAt: number,
  peakGain: number,
  durationSeconds: number,
): void {
  const attackSeconds = Math.min(0.012, durationSeconds / 3);
  gain.gain.setValueAtTime(SILENCE_FLOOR_GAIN, startAt);
  gain.gain.linearRampToValueAtTime(peakGain, startAt + attackSeconds);
  gain.gain.exponentialRampToValueAtTime(SILENCE_FLOOR_GAIN, startAt + durationSeconds);
}

function playToneLayer(audio: AudioContext, layer: ToneLayer, playheadSeconds: number): void {
  const startAt = playheadSeconds + layer.startAfterSeconds;
  const oscillator = audio.createOscillator();
  const gain = audio.createGain();

  oscillator.type = layer.waveform;
  oscillator.frequency.setValueAtTime(layer.fromHz, startAt);
  if (layer.toHz !== layer.fromHz) {
    oscillator.frequency.exponentialRampToValueAtTime(layer.toHz, startAt + layer.durationSeconds);
  }

  scheduleEnvelope(gain, startAt, layer.peakGain, layer.durationSeconds);

  oscillator.connect(gain).connect(audio.destination);
  oscillator.start(startAt);
  oscillator.stop(startAt + layer.durationSeconds + 0.02);
}

function playNoiseLayer(audio: AudioContext, layer: NoiseLayer, playheadSeconds: number): void {
  const source = audio.createBufferSource();
  const bandpass = audio.createBiquadFilter();
  const gain = audio.createGain();

  source.buffer = noiseBufferFor(audio);
  bandpass.type = 'bandpass';
  bandpass.Q.value = 0.9;
  bandpass.frequency.setValueAtTime(layer.fromCutoffHz, playheadSeconds);
  bandpass.frequency.exponentialRampToValueAtTime(
    layer.toCutoffHz,
    playheadSeconds + layer.durationSeconds,
  );

  scheduleEnvelope(gain, playheadSeconds, layer.peakGain, layer.durationSeconds);

  source.connect(bandpass).connect(gain).connect(audio.destination);
  source.start(playheadSeconds);
  source.stop(playheadSeconds + layer.durationSeconds + 0.02);
}

export function playSound(name: SoundName): void {
  if (!soundIsEnabled) return;

  const voice = SOUND_LIBRARY[name];
  const audio = openAudioContext();
  if (!audio) return;

  try {
    const playheadSeconds = audio.currentTime + 0.005;
    for (const layer of voice.tones) playToneLayer(audio, layer, playheadSeconds);
    if (voice.noise) playNoiseLayer(audio, voice.noise, playheadSeconds);
  } catch {
    soundIsEnabled = false;
  }
}

export function soundEnabled(): boolean {
  return soundIsEnabled;
}

export function setSoundEnabled(enabled: boolean): void {
  soundIsEnabled = enabled;

  try {
    window.localStorage.setItem(SOUND_PREFERENCE_STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    /* empty */
  }

  if (enabled) openAudioContext();
  for (const listener of soundPreferenceListeners) listener(enabled);
}

export function useSoundPreference(): { enabled: boolean; toggle: () => void } {
  const [enabled, setEnabled] = useState(soundIsEnabled);

  useEffect(() => {
    soundPreferenceListeners.add(setEnabled);
    setEnabled(soundIsEnabled);
    return () => {
      soundPreferenceListeners.delete(setEnabled);
    };
  }, []);

  const toggle = useCallback(() => {
    const next = !soundEnabled();
    setSoundEnabled(next);
    if (next) playSound('targetPressed');
  }, []);

  return { enabled, toggle };
}

export function useSoundOnMount(name: SoundName, whenReady = true): void {
  useEffect(() => {
    if (!whenReady) return;
    playSound(name);
  }, [name, whenReady]);
}

export const soundedPointerHandlers = {
  onPointerEnter: () => playSound('pointerCrossedTarget'),
  onPointerDown: () => playSound('targetPressed'),
} as const;
